import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../../../../app/hooks';
import { setAskAicaSelectedSources } from '../../../../../../../../store/dashboard/askAica/actions';

import DataSourceSelector from '../DataSourceSelector/DataSourceSelector';

import StopChatIcon from '../../../../../../../../assets/Svg/StopChatIcon';

import styles from './AskAicaInputBox.module.scss';

interface AskAicaInputBoxProps {
    onSendMessage: (message: string) => (() => void) | undefined;
    isLoading: boolean;
}

const AskAicaInputBox: React.FC<AskAicaInputBoxProps> = ({ onSendMessage, isLoading = false }) => {
    const abortFunctionRef = useRef<(() => void) | undefined>(undefined);

    const dispatch = useAppDispatch();

    const { messages, isLoadingChatHistory, uiState } = useAppSelector((state) => state.dashboard.askAicaReducer);
    const { selectedSources } = uiState;

    const [message, setMessage] = useState('');

    // Array of random greeting texts
    const greetingTexts = [
        'Ready to unlock business insights?',
        "Let's dive into the data together",
        'What would you like to explore today?',
        'Your AICA-powered business intelligence awaits',
        'Ready to discover what the data reveals?',
        "Let's transform your questions into insights",
    ];

    // Get a random greeting text
    const getRandomGreeting = () => {
        return greetingTexts[Math.floor(Math.random() * greetingTexts.length)];
    };

    // State for the current greeting text
    const [greetingText] = useState(() => getRandomGreeting());

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    }, [message]);

    // Auto-focus textarea when component mounts
    useEffect(() => {
        // Delay focus to ensure modal animation is complete
        const focusTimeout = setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        }, 350); // Wait for animation to complete (300ms) + small buffer

        return () => clearTimeout(focusTimeout);
    }, []);

    const stopChat = () => {
        try {
            abortFunctionRef.current?.();
        } catch (error) {
            console.error('Error stopping chat:', error);
        } finally {
            abortFunctionRef.current = undefined;
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (message.trim() && !isLoading) {
            abortFunctionRef.current = onSendMessage(message.trim());
            setMessage('');
        } else if (isLoading) {
            stopChat();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handleDataSourceChange = (sources: string[]) => {
        dispatch(setAskAicaSelectedSources(sources));
    };

    if (messages.length === 0 && isLoadingChatHistory) return <></>;

    return (
        <>
            <div
                className={clsx(styles.InputArea, {
                    [styles.InputCentered]: messages.length === 0 && !isLoadingChatHistory,
                })}
            >
                {messages.length === 0 && !isLoadingChatHistory && <div className={styles.EmptyText}>{greetingText}</div>}
                {/* Message Input Form */}
                <form onSubmit={handleSubmit} className={styles.InputForm}>
                    <div className={styles.InputWrapper}>
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask anything"
                            className={styles.MessageInput}
                            disabled={isLoading}
                            rows={1}
                        />
                        <button
                            type="submit"
                            className={`${styles.SendButton} ${
                                isLoading || (message.trim() && selectedSources.length > 0) ? styles.Active : ''
                            }`}
                            disabled={!isLoading && (!message.trim() || selectedSources.length === 0)}
                        >
                            {isLoading ? <StopChatIcon /> : <span className={styles.SendIcon}>→</span>}
                        </button>
                    </div>
                </form>

                {/* Data Source Selector */}
                <div className={styles.DataSourceRow}>
                    <DataSourceSelector
                        selectedSources={selectedSources}
                        onSelectionChange={handleDataSourceChange}
                        onClose={() => {}}
                        openAbove={true}
                        buttonStyle="input"
                    />
                </div>
            </div>
            <div className={styles.WarningText}>AICA can make mistakes. Check important info</div>
        </>
    );
};

export default AskAicaInputBox;
